import React, { useState, useMemo } from 'react';
import { Chevron, Tick } from '../../components/Glyph';
import PageBar, { SearchInput, DESKTOP_QUERY } from '../../components/PageBar';
import useMediaQuery from '../../hooks/useMediaQuery';
import Dialog from '../../components/Dialog';
import { IconDoc } from '../../components/dialogIcons';
import { dayShort } from '../../lib/when';
import { metaOf, closureReason } from '../../lib/tasks';
import { QUEUES, rowName, rowSays, actionsFor } from './queues';
import CountReadThrough from './CountReadThrough';
import ListPanel from '../../components/ListPanel';
import Pill from '../../components/Pill';
import ReasonDialog from './ReasonDialog';

/*
  The Review Hub, as a page.

  Built 5 September 2026 for job 11, to Group E's five approved drawings.

  **It was a dialog with five tabs, and on a phone it was unusable.** A location
  name was squeezed into about 90 pixels beside two buttons, so
  "SAT-CLEAR SPRING PHYSICAL THERAPY SOUTH" took five lines and its date took two
  more. Photographed before anything changed:
  `docs/design/live/admin-overview-open-390.png` (deleted 12 September 2026; kept in the tag before-phase-3-cleanup).

  **It is the list-and-panel shape** Group D settled: the queue down the left,
  the thing you picked on the right, and below 1024 the panel above the list.
  Nothing new was invented for it. What changes per queue is what the panel holds
  and what its two buttons say, which is `queues.js`.

  **The one thing that had to survive the change**, in the build order's own
  words: **Approve is locked until you have read every line of the sheet.** That
  is a deliberate guard against approving a count nobody read, it was in the
  running app, and it is not in the mockup this page was drawn from.
*/


// One labelled fact in the panel's grid. Two columns on a desk, one below 640,
// which is what the drawings show.
function Fact({ label, children, tone }) {
  return (
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: tone || 'var(--text-primary)', marginTop: 3 }}>{children}</span>
    </span>
  );
}

// Somebody's own words, kept as they wrote them.
function Said({ who, words }) {
  if (!words) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
        {who} said
      </div>
      <div style={{ marginTop: 6, borderLeft: '3px solid var(--info)', borderRadius: '0 10px 10px 0', background: 'var(--hover)', padding: '12px 14px', fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.55 }}>
        {words}
      </div>
    </div>
  );
}

export default function ReviewHubPage({
  cycle, lineCounts = {}, allCounts = [], valuations = {},
  pendingCounts = [], editRequests = [], closures = [], pickedUpLocations = [], ownershipTransfers = [],
  onApproveCount, onRejectCount, onApproveEdit, onDenyEdit, onConfirmClosure, onDenyClosure,
  onMarkReviewed, onTransferOwnership, onDenyTransfer, onReleaseLocation,
  queue: queueFromPage, onQueueChange, pick,
}) {
  /*
    **Which queue is showing can come from outside**, because the things that
    used to open the dialog on a particular tab, a count row on Cycle Details
    and the rows on Home, now send you to this page instead and have to be able
    to say which queue they meant.
  */
  const [ownQueue, setOwnQueue] = useState('counts');
  const queue = queueFromPage || ownQueue;
  const setQueue = k => { setOwnQueue(k); onQueueChange?.(k); };
  /*
    **Something can send you here about one particular row.** "View count" on a
    location that has been handed in means that count, and it used to open the
    dialog with that row expanded. It opens the page on that row now.
  */
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const chosen = picked || pick || null;
  const [search, setSearch] = useState('');
  const [readTo, setReadTo] = useState({});   // count id -> read to the end

  const rowsByQueue = {
    counts:        pendingCounts,
    edit_requests: editRequests,
    closures:      closures,
    picked_up:     pickedUpLocations,
    ownership:     ownershipTransfers,
  };
  const counts = Object.fromEntries(QUEUES.map(q => [q.key, (rowsByQueue[q.key] || []).length]));
  const waiting = Object.values(counts).reduce((a, b) => a + b, 0);

  const rows = useMemo(() => {
    const list = rowsByQueue[queue] || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(r => {
      const { code, name } = rowName(r, queue);
      const meta = metaOf(r);
      return (code + ' ' + name + ' ' + (r.rep?.full_name || '') + ' ' + (meta.rep_name || '')).toLowerCase().includes(q);
    });
  }, [queue, search, pendingCounts, editRequests, closures, pickedUpLocations, ownershipTransfers]);

  const row = rows.find(r => r.id === chosen) || rows[0] || null;
  const meta = row ? metaOf(row) : {};
  const names = { rep: row?.rep?.full_name || meta.rep_name };
  const act = row ? actionsFor(queue, row, names) : null;

  // The gate. Only Counts has one today; a closure with a final count on it
  // gets the same one, which is what the drawing says.
  const gated = queue === 'counts';
  const readThrough = !gated || !!readTo[row?.id];

  /*
    **The way out asks for a reason first**, on the four queues where somebody is
    told no. The rep sees exactly what is written, which is what the old rows
    did with a box that unfolded inside them, and what this page would have
    quietly dropped: it sent an empty string.

    Picked up is the one that does not, because taking a location back is not a
    refusal of anything: nobody asked.
  */
  const [reasonFor, setReasonFor] = useState(null);
  function runAway() {
    if (!row) return;
    if (queue === 'picked_up') return onReleaseLocation?.(row);
    setReasonFor(row.id);
  }
  async function sendReason(words) {
    if (!row) return;
    if (queue === 'counts')        await onRejectCount?.(row, words, { repId: row.rep_id, locationName: row.warehouse?.name });
    else if (queue === 'edit_requests') await onDenyEdit?.(row, words);
    else if (queue === 'closures')      await onDenyClosure?.(row, closureMeta(row), words);
    else                                await onDenyTransfer?.(row, words);
    setReasonFor(null);
  }
  function runAct() {
    if (!row) return;
    if (queue === 'counts')        return onApproveCount?.(row.id);
    if (queue === 'edit_requests') return onApproveEdit?.(row);
    if (queue === 'closures')      return onConfirmClosure?.(row, closureMeta(row), '');
    if (queue === 'picked_up')     return onMarkReviewed?.(row.id);
    return onTransferOwnership?.(row);
  }
  function closureMeta(r) {
    const m = metaOf(r);
    return { warehouseId: r.warehouse_id, repId: r.rep_id, locationName: r.warehouse?.name || m.location_name };
  }

  return (
    <>
      {/* **The five queues are the page's own sub-tabs**, which is the shell's
          existing mechanism rather than a new row of chips: the drawing's chip
          row and the bar's sub-tabs are the same thing, and building a second
          one is how six menus happened. */}
      <PageBar
        title="Review Hub"
        meta={waiting + ' thing' + (waiting === 1 ? '' : 's') + ' waiting on you'}
        subTabs={isDesktop ? QUEUES.map(q => ({ key: q.key, label: q.label, count: counts[q.key] })) : undefined}
        activeSubTab={queue}
        onSubTabChange={k => { setQueue(k); setPicked(null); }}
        controls={
          <>
            <SearchInput value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search a location or a rep" />
            {/* **Below 1024 the five queues are one button that opens a
                picker**, which is what the approved drawing shows and what rule
                34 means when it says the queue picker is a dialog too. Five
                tabs do not fit across a phone: photographed at 390 with Picked
                up cut in half and Ownership off the edge entirely. */}
            {!isDesktop && (
              <button type="button" className="btn btn-primary" style={{ minHeight: 44 }}
                onClick={() => setPickerOpen(true)}>
                {QUEUES.find(q => q.key === queue)?.label} {counts[queue]}
                <Chevron size={12} />
              </button>
            )}
          </>
        }
      />

      {reasonFor && row && (
        <ReasonDialog
          {...reasonWords(queue, row, metaOf(row))}
          onSend={sendReason}
          onClose={() => setReasonFor(null)}
        />
      )}

      {pickerOpen && (
        <Dialog
          kind="info" icon={<IconDoc />} size="question"
          title="Which queue"
          says={waiting + ' things are waiting on you.'}
          onClose={() => setPickerOpen(false)}
          footer={<button className="btn btn-quiet" onClick={() => setPickerOpen(false)}>Close</button>}
        >
          {QUEUES.map(q => (
            <button key={q.key} type="button"
              onClick={() => { setQueue(q.key); setPicked(null); setPickerOpen(false); }}
              className={'hub-pick' + (queue === q.key ? ' is-on' : '')}>
              <span className={'hub-pick-bar tone-' + q.tone} />
              <span className="hub-pick-label">{q.label}</span>
              <span className="hub-pick-n">{counts[q.key]}</span>
              {queue === q.key && <span className="hub-pick-tick"><Tick size={14} /></span>}
            </button>
          ))}
        </Dialog>
      )}

      {/* **The shared list-and-panel shape**, since 5 September 2026. This page
          wrote its own on the day it was built, which made four copies of one
          shape in OnHand; there is one now, and it owns the rule that the panel
          goes above the list below 1024. */}
      <ListPanel
        selected={row?.id}
        listWidth="380px"
        list={
          rows.length === 0 ? (
            <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
              {search ? 'Nothing here matches "' + search + '".' : 'Nothing is waiting in this queue.'}
            </div>
          ) : rows.map(r => {
            const { code, name } = rowName(r, queue);
            const on = row && r.id === row.id;
            return (
              <button key={r.id} type="button" onClick={() => setPicked(r.id)}
                className={'hub-row' + (on ? ' is-on' : '')}>
                <span className="hub-row-code">{code}</span>
                <span className="hub-row-name">{name}</span>
                <span className="hub-row-says">{rowSays(r, queue, dayShort, lineCounts[r.id])}</span>
                {metaOf(r).urgency === 'urgent' && <span className="hub-row-flag"><Pill tone="ns">Urgent</Pill></span>}
              </button>
            );
          })
        }
        empty="Nothing to show. Pick a queue with something in it."
        panel={
          !row ? null : (
            <div className="hub-panel">
              <div className="hub-panel-head">
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                    {rowName(row, queue).code}
                  </span>
                  <span className="oh-title" style={{ display: 'block', fontSize: 21, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: -0.4, marginTop: 2 }}>
                    {rowName(row, queue).name}
                  </span>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {panelLine(row, queue)}
                  </span>
                </span>
                <Pill tone={QUEUES.find(q => q.key === queue)?.tone}>{QUEUES.find(q => q.key === queue)?.label}</Pill>
              </div>

              <div className="hub-panel-body">
                <div className="hub-facts">{facts(row, queue, lineCounts, { allCounts, valuations })}</div>

                <Said who={firstName(saidBy(row, queue))} words={saidWords(row, queue)} />

                {queue === 'counts' && (
                  <CountReadThrough count={row} onReadToEnd={() => setReadTo(p => ({ ...p, [row.id]: true }))} />
                )}

                <div style={{ marginTop: 16, borderRadius: 12, padding: '13px 15px', fontSize: 13, lineHeight: 1.55, background: bandBg(queue), color: bandFg(queue) }}>
                  {act.says}
                </div>
                {act.aside && (
                  <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{act.aside}</div>
                )}

                {gated && !readThrough && (
                  <div style={{ marginTop: 14, borderRadius: 12, padding: '13px 15px', fontSize: 13, lineHeight: 1.55, background: 'var(--warning-bg)', color: 'var(--warning-ink)' }}>
                    Read to the end of the lines to unlock Approve and Send back. A count nobody has read does not get approved.
                  </div>
                )}
              </div>

              <div className="hub-panel-foot">
                <button className={'btn ' + (act.actTone === 'ns' ? 'btn-quiet' : 'btn-quiet')}
                  onClick={runAway} disabled={gated && !readThrough}>
                  {act.away}
                </button>
                <button className={'btn ' + actClass(act.actTone)} onClick={runAct} disabled={gated && !readThrough}>
                  {act.act}
                </button>
              </div>
            </div>
          )
        }
      />
    </>
  );
}

function actClass(tone) {
  if (tone === 'ns')     return 'btn-danger';
  if (tone === 'transfer') return 'btn-primary';
  if (tone === 'app')    return 'btn-approve';
  return 'btn-primary';
}
function bandBg(queue) {
  if (queue === 'ownership') return 'var(--info-bg)';
  if (queue === 'picked_up') return 'var(--success-bg)';
  return 'var(--hover)';
}
function bandFg(queue) {
  if (queue === 'ownership') return 'var(--info-ink)';
  if (queue === 'picked_up') return 'var(--success-ink)';
  return 'var(--text-secondary)';
}
function firstName(n) { return String(n || 'They').trim().split(/\s+/)[0]; }

function saidBy(row, queue) {
  const m = metaOf(row);
  if (queue === 'ownership') return m.requesting_rep_name;
  return m.rep_name;
}
function saidWords(row, queue) {
  const m = metaOf(row);
  if (queue === 'edit_requests') return m.details || m.reason || '';
  if (queue === 'closures')      return m.notes || '';
  if (queue === 'ownership')     return m.reason || '';
  return '';
}
function panelLine(row, queue) {
  const m = metaOf(row);
  const when = dayShort(row.created_at || row.submitted_at);
  if (queue === 'counts')        return 'Submitted ' + when + ' by ' + (row.rep?.full_name || m.rep_name || 'somebody');
  if (queue === 'closures')      return 'Flagged by ' + (m.rep_name || 'somebody') + ' on ' + when;
  if (queue === 'picked_up')     return (m.rep_name || 'Somebody') + ' picked it up on ' + when;
  if (queue === 'ownership')     return (m.requesting_rep_name || 'Somebody') + ' asked on ' + when;
  return (m.rep_name || 'Somebody') + ' asked on ' + when;
}

function facts(row, queue, lineCounts = {}, extra = {}) {
  const m = metaOf(row);
  const when = dayShort(row.created_at || row.submitted_at);
  if (queue === 'counts') {
    return (
      <>
        <Fact label="Rep">{row.rep?.full_name || m.rep_name || 'Nobody'}</Fact>
        <Fact label="Submitted">{when}</Fact>
        <Fact label="Region">{row.warehouse?.region?.name || '--'}</Fact>
        <Fact label="Lines">{lineCounts[row.id]?.total ?? '--'}</Fact>
      </>
    );
  }
  if (queue === 'edit_requests') {
    return (
      <>
        <Fact label="Who asked">{m.rep_name || '--'}</Fact>
        <Fact label="When">{when}</Fact>
        <Fact label="Urgency" tone={m.urgency === 'urgent' ? 'var(--critical-ink)' : undefined}>
          {m.urgency === 'urgent' ? 'Urgent' : 'Normal'}
        </Fact>
        <Fact label="The count">Submitted, locked</Fact>
      </>
    );
  }
  if (queue === 'closures') {
    /*
      **What OnHand knows was counted there, beside what the rep says.** The
      owner asked for this on 3 September 2026, and the two are labelled apart on
      purpose: one is the app's own record and the other is the rep typing from
      memory. They are allowed to disagree, and a disagreement is exactly what an
      administrator wants to see before closing a location for good.
    */
    const theirs = (extra.allCounts || []).find(c => c.warehouse?.id === row.warehouse_id);
    const value  = theirs ? (extra.valuations || {})[theirs.id] : null;
    return (
      <>
        <Fact label="Reason">{closureReason(m.reason) || '--'}</Fact>
        <Fact label="Region">{m.region || row.warehouse?.region?.name || '--'}</Fact>
        <Fact label="Last counted, per the rep">{plainDay(m.last_count_date)}</Fact>
        <Fact label="Final count done">{m.final_count_performed === 'yes' ? 'Yes' : m.final_count_performed === 'no' ? 'No' : '--'}</Fact>
        <Fact label="Stock retrieved" tone={m.inventory_retrieved === 'no' ? 'var(--critical-ink)' : undefined}>
          {m.inventory_retrieved === 'yes' ? 'Yes' : m.inventory_retrieved === 'partial' ? 'Some of it' : m.inventory_retrieved === 'no' ? 'No' : '--'}
        </Fact>
        <Fact label="Stock on the last count" tone={value ? 'var(--critical-ink)' : undefined}>
          {value
            ? '$' + Math.round(value).toLocaleString() + (theirs?.submitted_at ? ' - counted ' + dayShort(theirs.submitted_at) : '')
            : 'Nothing counted here this cycle'}
        </Fact>
      </>
    );
  }
  if (queue === 'picked_up') {
    return (
      <>
        <Fact label="Who">{m.rep_name || '--'}</Fact>
        <Fact label="When">{when}</Fact>
        <Fact label="Region">{m.region || row.warehouse?.region?.name || '--'}</Fact>
        <Fact label="Was assigned to">{m.previous_owner_name || 'Nobody'}</Fact>
      </>
    );
  }
  return (
    <>
      <Fact label="Holds it now" tone="var(--critical-ink)">{m.current_owner_name || '--'}</Fact>
      <Fact label="Asking for it" tone="var(--info-ink)">{m.requesting_rep_name || '--'}</Fact>
      <Fact label="When">{when}</Fact>
      <Fact label="The sheet">{m.count_status === 'submitted' ? 'Submitted' : 'In progress'}</Fact>
    </>
  );
}

/*
  What the reason dialog says in each of its four uses. The person is named in
  the act, because a button that names somebody is hard to press by accident.
*/
function reasonWords(queue, row, meta) {
  const first = n => String(n || 'the rep').trim().split(/\s+/)[0];
  const rep = first(row.rep?.full_name || meta.rep_name);
  const asking = first(meta.requesting_rep_name);
  if (queue === 'counts') {
    return {
      title: 'Send this count back',
      record: [row.warehouse?.name, row.rep?.full_name || meta.rep_name].filter(Boolean).join(' - '),
      says: 'The sheet goes back to ' + rep + ' to correct and submit again. ' + rep + ' sees exactly what you write.',
      label: 'What needs correcting',
      placeholder: 'Tell ' + rep + ' what to look at again',
      act: 'Send it back to ' + rep,
    };
  }
  if (queue === 'edit_requests') {
    return {
      title: 'Deny this request',
      record: [row.warehouse?.name || meta.location_name, meta.rep_name].filter(Boolean).join(' - '),
      says: 'The sheet stays locked. ' + rep + ' sees exactly what you write.',
      label: 'Why not',
      placeholder: 'Tell ' + rep + ' why the sheet stays as it is',
      act: 'Deny and tell ' + rep,
    };
  }
  if (queue === 'closures') {
    return {
      title: 'Deny this closure',
      record: [row.warehouse?.name || meta.location_name, meta.rep_name].filter(Boolean).join(' - '),
      says: 'The location stays open and keeps getting count sheets. ' + rep + ' sees exactly what you write.',
      label: 'Why not',
      placeholder: 'Tell ' + rep + ' why it stays open',
      act: 'Deny and tell ' + rep,
    };
  }
  return {
    title: 'Deny this transfer',
    record: [row.warehouse?.name || meta.location_name, meta.requesting_rep_name].filter(Boolean).join(' - '),
    says: 'The sheet stays with ' + first(meta.current_owner_name) + '. ' + asking + ' sees exactly what you write.',
    label: 'Why not',
    placeholder: 'Tell ' + asking + ' why the sheet stays where it is',
    act: 'Deny and tell ' + asking,
  };
}

/*
  A plain day, read as three numbers rather than as a moment.

  **A browser reads "2026-05-30" as midnight in Greenwich**, which in Texas is
  the evening before, so the ordinary date reader prints the 29th to everybody
  who uses OnHand. This is the same rule the count's due date follows.
*/
export function plainDay(text) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(text || ''));
  if (!m) return text || '--';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return Number(m[3]) + ' ' + MONTHS[Number(m[2]) - 1] + ' ' + m[1];
}
